
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, IndianRupee, ShoppingCart, Users, Package } from "lucide-react";

export default function AdminAnalyticsPage() {
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    revenueGrowth: 0,
    totalOrders: 0,
    ordersGrowth: 0,
    totalCustomers: 0,
    customersGrowth: 0,
    averageOrderValue: 0,
    topProducts: [],
    recentOrders: [],
  });

  // Mock data - replace with actual API call
  useEffect(() => {
    setAnalytics({
      totalRevenue: 1250000,
      revenueGrowth: 12.5,
      totalOrders: 342,
      ordersGrowth: 8.3,
      totalCustomers: 1250,
      customersGrowth: 15.2,
      averageOrderValue: 3650,
      topProducts: [],
      recentOrders: [],
    });
  }, []);

  const stats = [
    {
      title: "Total Revenue",
      value: `₹${analytics.totalRevenue.toLocaleString()}`,
      change: analytics.revenueGrowth,
      icon: IndianRupee,
      color: "bg-green-500",
    },
    {
      title: "Total Orders",
      value: analytics.totalOrders,
      change: analytics.ordersGrowth,
      icon: ShoppingCart,
      color: "bg-blue-500",
    },
    {
      title: "Total Customers",
      value: analytics.totalCustomers,
      change: analytics.customersGrowth,
      icon: Users,
      color: "bg-purple-500",
    },
    {
      title: "Avg Order Value",
      value: `₹${analytics.averageOrderValue.toLocaleString()}`,
      change: 5.2,
      icon: Package,
      color: "bg-pink-500",
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold font-playfair mb-1">Analytics</h1>
        <p className="text-sm text-gray-600">Business insights and performance metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-lg shadow-sm p-3 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-2">
                <div className={`${stat.color} p-1.5 rounded-md`}>
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
                {stat.change >= 0 ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <TrendingUp className="w-3 h-3" />
                    <span className="text-xs font-medium">+{stat.change}%</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-red-600">
                    <TrendingDown className="w-3 h-3" />
                    <span className="text-xs font-medium">{stat.change}%</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-600 mb-1">{stat.title}</p>
              <p className="text-lg font-bold text-gray-900">{stat.value}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Charts Placeholder */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
        >
          <h2 className="text-base font-bold mb-3">Revenue Overview</h2>
          <div className="h-48 flex items-center justify-center text-sm text-gray-400">
            Chart placeholder - Integrate with Chart.js or Recharts
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
        >
          <h2 className="text-base font-bold mb-3">Orders Trend</h2>
          <div className="h-48 flex items-center justify-center text-sm text-gray-400">
            Chart placeholder - Integrate with Chart.js or Recharts
          </div>
        </motion.div>
      </div>
    </div>
  );
}
